<template>
    <div>
        <div class="row" v-if="propertyAvailable('valid_from') || propertyAvailable('valid_to')">
            <div class="col-md-6" v-if="propertyAvailable('valid_from')">
                <datepicker
                    v-model="valid_from"
                    :disabled="readonly || !playbackPrivilege || isCalendar"
                    :label="translations.valid_from"
                    :max="validFromMaxLimit"
                    hideDetails
                    :placeholder="!properties.validityDetached ? validFromPlaceholderLocalized : null"/>
            </div>
            <div class="col-md-6" v-if="propertyAvailable('valid_to')">
                <datepicker
                    v-model="valid_to"
                    :disabled="readonly || !playbackPrivilege || isCalendar"
                    :label="translations.valid_to"
                    :min="validToMinLimit"
                    hideDetails
                    :placeholder="!properties.validityDetached ? validToPlaceholderLocalized : null"
                    align-right/>
            </div>
        </div>

        <div class="row" v-if="propertyAvailable('valid_time_from') || propertyAvailable('valid_time_to')">
            <div class="col-md-6" v-if="propertyAvailable('valid_time_from')">
                <timepicker
                    :id="'valid-time-from-' + properties.index"
                    :label="translations.from()"
                    v-model="valid_time_from"
                    picker-family="valid-time"
                    hide-format-hint
                    :disabled="readonly || !playbackPrivilege"
                    :clearable="!isCalendar"
                />
            </div>
            <div class="col-md-6" v-if="propertyAvailable('valid_time_to')">
                <timepicker
                    :id="'valid-time-to-' + properties.index"
                    :label="translations.to()"
                    v-model="valid_time_to"
                    align-right
                    picker-family="valid-time"
                    hide-format-hint
                    :disabled="readonly || !playbackPrivilege"
                    :clearable="!isCalendar"
                />
            </div>
        </div>

        <div class="row" v-if="propertyAvailable('valid_time_from') && !isCalendar">
            <div class="col-md-12">
                <Checkbox
                    class="mr-2"
                    :binary="true"
                    :modelValue="properties.literal_times"
                    @click="updateProperty('literal_times', !properties.literal_times)"
                    :label="translations.literal_times"
                    :hint="translations.literal_times"
                />
                {{ translations.literal_times }}
            </div>
        </div>

        <div class="col-md-12" v-if="isCalendar">{{ translations.calendarDescription }}</div>

        <div class="row" v-if="propertyAvailable('duration_type') || propertyAvailable('duration') || propertyAvailable('display_runs')">
            <div class="col-md-8" v-if="propertyAvailable('duration_type')">
                <duration-type-setting
                    :value="properties.duration_type"
                    @input="updateProperty('duration_type', $event)"
                    :disabled="readonly || !playbackPrivilege"
                    :element-duration-disabled="!propertyAvailable('display_runs')"
                    :duration-element="durationElement"
                />
            </div>
            <div class="col-md-4" v-if="propertyAvailable('display_runs') && properties.duration_type === 2">
                <number-field
                    :value="properties.display_runs"
                    @input="updateProperty('display_runs', $event)"
                    :label="translations.display_runs"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('display_runs')"
                    :hint="translations.display_runs_description"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto"
                />
            </div>
            <div class="col-md-4" v-if="propertyAvailable('duration') && properties.duration_type !== 2">
                <number-field
                    v-model="duration"
                    :label="translations.seconds"
                    :disabled="properties.duration_type === 1 || readonly || !playbackPrivilege"
                    :limits="getFieldLimits('duration')"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto"
                />
            </div>
        </div>

        <div class="row" v-if="propertyAvailable('loop') || propertyAvailable('weekdays')">
            <div
                v-if="propertyAvailable('loop')"
                :class="(propertyAvailable('weekdays') && !isCalendar) ? 'col-md-6' : 'col-md-12'"
            >
                <number-field
                    v-model="loop"
                    :label="translations.loop"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('loop')"
                    :hint="translations.loop_description"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
            <div
                v-if="propertyAvailable('weekdays') && !isCalendar"
                :class="propertyAvailable('loop') ? 'col-md-6' : 'col-md-12'"
            >
                <weekdays-setting
                    v-model="weekdays"
                    :disabled="readonly || !playbackPrivilege"
                />
            </div>
        </div>

        <div class="row" v-if="propertyAvailable('default_duration')">
            <div class="col-md-12">
                <number-field
                    v-model="defaultDuration"
                    :label="translations.duration_default + ' (' + translations.seconds + ')'"
                    :disabled="readonly || !playbackPrivilege"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
        </div>

        <div class="row">
            <div :class="properties.transition !== '1' ? 'col-md-6' : 'col-md-12'" v-if="propertyAvailable('transition')">
                <transition-setting
                    :value="properties.transition"
                    @input="updateProperty('transition', $event)"
                    :disabled="readonly || !playbackPrivilege"
                />
            </div>
            <div class="col-md-6" v-if="properties.transition !== '1' && propertyAvailable('transition_length')">
                <number-field
                    :value="properties.transition_length"
                    @input="updateProperty('transition_length', $event)"
                    :label="translations.transition_length"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('transition_length')"
                    placeholder="0.4"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12" v-if="propertyAvailable('synchronize_playback') &&
                (propertyAvailable('synchronize_playback')['value'] === 'rich' || propertyAvailable('synchronize_playback')['value'] === 'readonly')">
                <synchronize-playback-setting
                    :value="properties.synchronize_playback"
                    @input="updateProperty('synchronize_playback', $event)"
                    :disabled="(propertyAvailable('synchronize_playback')['value'] === 'rich' ? readonly : true) || !playbackPrivilege"
                />
            </div>
        </div>

        <div class="row">
            <div :class="[3, 4, 5, 6].includes(properties.smart_loop) ? 'col-md-8' : 'col-md-12'" v-if="propertyAvailable('smart_loop')">
                <smart-loop-setting
                    :value="properties.smart_loop"
                    @input="updateProperty('smart_loop', $event)"
                    :disabled="readonly || !playbackPrivilege"
                    @change="properties.smart_loop_number = null"
                />
            </div>
            <div class="col-md-4" v-if="propertyAvailable('smart_loop_number') && [3, 4, 5, 6].includes(properties.smart_loop)">
                <smart-loop-number-setting
                    :value="properties.smart_loop_number"
                    @input="updateProperty('smart_loop_number', $event)"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('smart_loop_number')"
                    :smart-loop="properties.smart_loop"
                />
            </div>
        </div>

        <div class="row">
            <div class="col-md-12" v-if="propertyAvailable('description')">
                <description-setting
                    :value="properties.description"
                    @input="updateProperty('description', $event)"
                    :disabled="readonly || !playbackPrivilege"
                />
            </div>
        </div>
    </div>
</template>

<script>
import commonDefinitionMixin from "./commonDefinitionMixin";
import utility from "../../../utility";
import {dateUSToDateISO, localizeDate} from '../../../../utils/date';
import {filterElementsByDay, subtractOneDay, validateTimeChange} from "../publish/calendar/calendarUtils";
import toaster from "../../common/toaster";
import DurationTypeSetting from "./playback/duration-type-setting.vue";
import WeekdaysSetting from "./playback/weekdays-setting.vue";
import TransitionSetting from "./playback/transition-setting.vue";
import SynchronizePlaybackSetting from "./playback/synchronize-playback-setting.vue";
import SmartLoopSetting from "./playback/smart-loop-setting.vue";
import DescriptionSetting from "./playback/description-setting.vue";
import SmartLoopNumberSetting from "./playback/smart-loop-number-setting.vue";
import {calcPagedDuration} from '../../../../utils/publish';
import {secondsToTimeString} from '../../../../utils/time';
import Checkbox from "../common/PrimeVue/Checkbox.vue";

export default {
    components: {
        Checkbox,
        SmartLoopNumberSetting,
        DescriptionSetting, SmartLoopSetting, SynchronizePlaybackSetting, TransitionSetting, WeekdaysSetting, DurationTypeSetting
    },
    mixins: [
        commonDefinitionMixin,
    ],
    props: {
        value: null,
        definition: null,
        is_parent: Boolean,
        is_publish: String,
        clearable: Boolean,
        readonly: Boolean,
        durationPrivilege: Boolean,
        playbackPrivilege: Boolean,
        default_duration: String,
        activeLayer: undefined,
        activeElementId: null,
    },
    computed: {
        properties() {
            return _.cloneDeep(this.value) || {};
        },
        isCalendar() {
            if (this.activeLayer !== undefined) {
                return this.activeLayer.is_calendar;
            }
            return false;
        },
        translations() {
            return {
                literal_times: Lang.langGet('Limit first/last day only'),
                valid_time: Lang.langGet("Show element..."),
                valid_from: Lang.langGet("Not before"),
                valid_to: Lang.langGet("Not after"),
                duration_default: Lang.langGet("Standard duration"),
                display_runs: Lang.langGet("Runs"),
                display_runs_description: Lang.langGet("Indicates, how often the element is repeated with its own duration."),
                transition_length: Lang.langGet("Transition length"),
                loop: Lang.langGet("Show every X loops"),
                loop_description: Lang.langGet("Indicates on which loop the element will be played. E.g., 2 means that the element will be played in one loop, then will not be played in the next, etc."),
                seconds: Lang.langChoice("Second|Seconds", 2),
                yes: Lang.langGet("Yes"),
                no: Lang.langGet("No"),
                calendarDescription: Lang.langGet('To edit an event\'s time or date, simply drag the entire event to a new slot or adjust its duration by dragging the borders in the calendar.'),
                from() {
                    const string = Lang.langGet("Show from");
                    return string.charAt(0).toUpperCase() + string.slice(1);
                },
                to() {
                    const string = Lang.langGet("Show to");
                    return string.charAt(0).toUpperCase() + string.slice(1);
                },
            }
        },
        elements() {
            return this.activeLayer.elements.map(element => {
                return {
                    id: element.id,
                    name: element.data.name,
                    validTimeFrom: element.data.valid_time_from,
                    validTimeTo: element.data.valid_time_to,
                    startDate: element.data.valid_from,
                    endDate: element.data.valid_to,
                };
            });
        },
        weekdays: {
            get() {
                return this.properties.weekdays;
            },
            set(newVal) {
                this.updateProperty('weekdays', newVal);
            }
        },
        valid_from: {
            get() {
                const props = this.properties;

                if (this.is_publish !== '0') return props.valid_from;
                if (this.is_parent) return props.parameters?.['valid-from'];

                return props.valid_from || props.validity.from;
            },
            set(val) {
                if (val && this.validToOrPlaceholder && (new Date(dateUSToDateISO(val)) > new Date(dateUSToDateISO(this.validToOrPlaceholder)))) {
                    this.updateProperty('valid_to', val);
                }

                const props = this.properties;
                this.updateProperty('validityDetached', !!(val || this.valid_to));

                if (this.is_publish === '0' && this.is_parent) {
                    if (!props.parameters) props.parameters = {};
                    this.value.parameters['valid-from'] = val;
                    return;
                }

                this.updateProperty('valid_from', val);
                if (props.valid_to_placeholder && !props.valid_to && val) {
                    this.updateProperty('valid_to', props.valid_to_placeholder);
                }

                if (this.is_publish === '0') {
                    this.updateValidity();
                }
            }
        },
        valid_to: {
            get() {
                const props = this.properties;

                if (this.is_publish !== '0') return props.valid_to;

                if (this.is_parent) return props.parameters?.['valid-to'];

                return props.valid_to || props.validity.to;
            },
            set(val) {
                if (val && this.validFromOrPlaceholder && (new Date(dateUSToDateISO(val)) < new Date(dateUSToDateISO(this.validFromOrPlaceholder)))) {
                    this.updateProperty('valid_from', val);
                }

                const props = this.properties;
                this.updateProperty('validityDetached', !!(val || this.valid_from));

                if (this.is_publish === '0' && this.is_parent) {
                    if (!props.parameters) props.parameters = {};
                    this.value.parameters['valid-to'] = val;
                    return;
                }

                this.updateProperty('valid_to', val);
                if (props.valid_from_placeholder && !props.valid_from && val) {
                    this.updateProperty('valid_from', props.valid_from_placeholder);
                }

                if (this.is_publish === '0') {
                    this.updateValidity();
                }
            }
        },
        valid_time_from: {
            get() {
                return this.properties.valid_time_from;
            },
            set(val) {
                if (!this.isCalendar) {
                    this.updateProperty('valid_time_from', val);
                    return;
                }

                // This means it was an all-day event
                if (!this.properties.valid_time_to && !this.properties.valid_time_from) {
                    this.updateProperty('valid_time_from', val);
                    this.updateProperty('valid_time_to', '23:59');

                    // if its multiple days
                    if (this.properties.valid_from !== this.properties.valid_to) {
                        const validToOneDayBack = subtractOneDay(this.properties.valid_to);
                        this.updateProperty('valid_to', validToOneDayBack);
                    }
                    return;
                }

                const day = this.valid_from || this.valid_to;
                const elementsOnDay = filterElementsByDay(this.elements, day);
                const result = validateTimeChange(elementsOnDay, this.activeElementId, val, undefined);
                if (result.valid) {
                    this.updateProperty('valid_time_from', val);
                } else {
                    toaster.error(result.reason);
                }
            }
        },
        valid_time_to: {
            get() {
                return this.properties.valid_time_to;
            },
            set(val) {
                if (!this.isCalendar) {
                    this.updateProperty('valid_time_to', val);
                    return;
                }

                // This means it was an all-day event
                if (!this.properties.valid_time_to && !this.properties.valid_time_from) {
                    this.updateProperty('valid_time_to', val);
                    this.updateProperty('valid_time_from', '00:00');

                    // if its multiple days
                    if (this.properties.valid_from !== this.properties.valid_to) {
                        const validToOneDayBack = subtractOneDay(this.properties.valid_to);
                        this.updateProperty('valid_to', validToOneDayBack);
                    }
                    return;
                }

                const day = this.valid_from || this.valid_to;
                const elementsOnDay = filterElementsByDay(this.elements, day);
                const result = validateTimeChange(elementsOnDay, this.activeElementId, undefined, val);
                if (result.valid) {
                    this.updateProperty('valid_time_to', val);
                } else {
                    toaster.error(result.reason);
                }
            }
        },
        duration: {
            get() {
                if (this.properties.duration_type === 0) {
                    return this.properties.duration;
                } else if (this.properties.duration_type === 1) {
                    if (this.default_duration) {
                        return this.default_duration;
                    } else {
                        return Laravel.default_duration;
                    }
                } else {
                    return this.properties.duration_original * (this.properties.duration_type === 2 ? this.properties.display_runs : 1);
                }
            },
            set(val) {
                if (this.properties.duration_type === 0) {
                    this.updateProperty('duration', val);
                }
            }
        },
        defaultDuration: {
            get() {
                let duration = this.properties.default_duration;
                return duration ? duration : Laravel.default_duration;
            },
            set(val) {
                this.updateProperty('default_duration', val);
            }
        },
        loop: {
            get() {
                return this.properties.loop;
            },
            set(val) {
                this.updateProperty('loop', val);
            }
        },
        durationElement() {
            return calcPagedDuration(this.properties);
        },
        inner_duration_element_formatted() {
            return this.inner_duration_element == '?' ? '' : `(${secondsToTimeString(this.inner_duration_element)})`;
        },
        validFromPlaceholderLocalized() {
            return localizeDate(this.properties.valid_from_placeholder);
        },
        validToPlaceholderLocalized() {
            return localizeDate(this.properties.valid_to_placeholder);
        },
        validFromOrPlaceholder() {
            return this.valid_from || this.properties.valid_from_placeholder;
        },
        validToOrPlaceholder() {
            return this.valid_to || this.properties.valid_to_placeholder;
        },
        singleDaySelected() {
            return this.validFromOrPlaceholder && (this.validFromOrPlaceholder === this.validToOrPlaceholder);
        },
        validFromMaxLimit() {
            return this.singleDaySelected ? null : this.validToOrPlaceholder;
        },
        validToMinLimit() {
            return this.singleDaySelected ? null : this.validFromOrPlaceholder;
        },
    },
    methods: {
        updateProperty(property, value) {
            if (this.value[property] === value) {
                return;
            }
            // console.log(`Updating property ${property}, with value [${value}]`);
            // this.$emit('input', {...this.value, [property]: value});
            this.value[property] = value;
        },
        localizeDate,
        updateValidity() {
            let valid_from = this.valid_from ?? '';
            let valid_to = this.valid_to ?? '';

            if (valid_from === '') {
                let date_to = valid_to ? utility.formatDate(valid_to, Laravel.activeDateformat) : null;
                let date_from = valid_from ? utility.formatDate(valid_from, Laravel.activeDateformat) : null;

                if (date_from > date_to) {
                    valid_from = '';
                }
            }
            if (valid_to === '') {
                let date_to = valid_to ? utility.formatDate(valid_to, Laravel.activeDateformat) : null;
                let date_from = valid_from ? utility.formatDate(valid_from, Laravel.activeDateformat) : null;

                if (date_from > date_to) {
                    valid_to = '';
                }
            }

            const validity = utility.checkValidity(valid_from, valid_to);
            this.updateProperty('validity', validity);
        },
        elementProperties() {
            return this.definition;
        }
    },
    mounted() {
        EaseEventBus.listen('updateDateTimeForSettings', element => {
            const currentData = {...this.properties};
            if (element.data.id === currentData.id) {
                this.value = {...currentData, ...element.data};
            }
        });
    }
}
</script>
